import React from 'react';

import {
  arrow,
  autoPlacement,
  FloatingArrow,
  FloatingOverlay,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import cx from 'classnames';
import { At, CalendarBlank, Question } from 'phosphor-react';

import { EDetailsMode } from 'components/Pages/Admin/Itinerary/Details/types';
import Preview from 'components/Pages/Admin/shared/Preview';
import Button from 'components/shared/Button';
import Icon from 'components/shared/Icon';
import { TabSelector, TabSelectorProps, TabLabel } from 'components/shared/TabSelector';
import Text from 'components/shared/Typography/Text';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { IPage } from 'utils/itineraryDetails/types';

import { mockedEmptyProposal } from './GeneralPopoverPagePreview.constants';
import s from './GeneralPopoverPagePreview.module.css';
import * as t from './GeneralPopoverPagePreview.types';

export const GeneralPopoverPagePreview = (props: t.GeneralPopoverPagePreviewProps) => {
  const arrowRef = React.useRef(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    middleware: [
      autoPlacement(),
      offset(10),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const tabs: TabSelectorProps['tabs'] = [
    {
      labelContent: <ChronologicalTab />,
      content: (
        <TabContent
          proposal={props.proposal}
          detailsMode={EDetailsMode.Chronological}
          key={EDetailsMode.Chronological}
        />
      ),
    },
    {
      labelContent: <ClassicTab />,
      content: <TabContent proposal={props.proposal} detailsMode={EDetailsMode.Original} key={EDetailsMode.Original} />,
    },
  ];

  return (
    <div>
      <Button
        htmlType="button"
        type="empty"
        anchorRef={refs.setReference}
        onClick={() => setIsOpen(!isOpen)}
        className={s.button}
      >
        <Text type="brandon" size="medium">
          Preview
        </Text>
        <Icon component={Question} className={s.icon} />
      </Button>
      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay onClick={() => setIsOpen(false)} />
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={s.portalWrapper}
            onClick={(e) => e.stopPropagation()}
          >
            <FloatingArrow ref={arrowRef} context={context} width={20} height={10} fill="white" />
            <TabSelector tabs={tabs} />
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

const ChronologicalTab = () => <TabLabel Icon={CalendarBlank} text="Calendar Builder" />;
const ClassicTab = () => <TabLabel Icon={At} text="Classic Builder" />;
const TabContent = (props: t.TabContentProps) => {
  const { isMobile, isTabletOrMobile } = useMediaBreakpoints();
  const isNewProposal = !props.proposal.id;

  React.useEffect(() => {
    if (isMobile) return;

    setTimeout(() => {
      const content: HTMLDivElement | null = document.querySelector('.landing-v2-custom-content');
      content?.style?.setProperty?.('--contentHeight', '100%');
    });
  }, []);

  return (
    <div className={cx(s.previewWrapper, isNewProposal && s.previewWrapperNew)}>
      <Preview
        deviceType={isTabletOrMobile ? 'mobile' : 'desktop'}
        fetchData={simulateThunk(props.proposal, props.detailsMode)}
        getIsSamplePreview={() => true}
        modalWrapperClassName={s.modalWrapperClassName}
      />
    </div>
  );
};

const simulateThunk: t.SimulateThunk = (proposal, detailsMode) => () => async () => {
  const isNewProposal = !proposal.id;

  let clonedItinerary = {
    ...proposal,
    // Getting only the first page for optimization render, we don't need all of them
    landing: { ...proposal.landing, pages: [proposal?.landing?.pages?.[0]] as IPage[] },
    detailsMode,
  } as any;

  if (isNewProposal) {
    clonedItinerary = {
      ...mockedEmptyProposal,
      detailsMode,
      title: proposal.title || 'THIS IS WHERE PROPOSAL TITLE WILL GO',
      travelDates: proposal.travelDates || 'Proposed Travel Dates Goes Here',
      preparedFor: proposal.preparedFor || 'Prepared For Text Goes Here',
    };
  }

  return Promise.resolve(clonedItinerary);
};
